import { forwardRef, ReactNode } from 'react';

import clsx from 'clsx';

export type ProgressCircleSize =
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge';
export type ProgressCircleColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'destructive';
export type ProgressCircleVariant = 'classic' | 'solid';

interface BaseProgressCircleProps {
  value?: number;
  max?: number;
  size?: ProgressCircleSize;
  color?: ProgressCircleColor;
  variant?: ProgressCircleVariant;
  children?: ReactNode;
  className?: string;
  showValue?: boolean;
  animate?: boolean;
  strokeWidthValue?: number;
}

export type ProgressCircleProps = Omit<
  React.SVGProps<SVGSVGElement>,
  'value' | 'children'
> &
  BaseProgressCircleProps;

const sizeMapping = {
  xsmall: {
    radius: 22,
    strokeWidth: 3,
    fontSize: 'text-xs',
  },
  small: {
    radius: 28,
    strokeWidth: 4,
    fontSize: 'text-sm',
  },
  medium: {
    radius: 36,
    strokeWidth: 5,
    fontSize: 'text-base',
  },
  large: {
    radius: 44,
    strokeWidth: 6,
    fontSize: 'text-lg',
  },
  xlarge: {
    radius: 52,
    strokeWidth: 7,
    fontSize: 'text-xl',
  },
  xxlarge: {
    radius: 60,
    strokeWidth: 8,
    fontSize: 'text-2xl',
  },
} as const;

function resolveColor(color: ProgressCircleColor): {
  strokeClass: string;
  backgroundClass: string;
} {
  switch (color) {
    case 'primary':
      return {
        strokeClass: `progress-circle-color-primary`,
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'secondary':
      return {
        strokeClass: 'progress-circle-color-secondary',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'success':
      return {
        strokeClass: 'progress-circle-color-success',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'warning':
      return {
        strokeClass: 'progress-circle-color-warning',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'error':
      return {
        strokeClass: 'progress-circle-color-error',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'info':
      return {
        strokeClass: 'progress-circle-color-info',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'destructive':
      return {
        strokeClass: 'progress-circle-color-destructive',
        backgroundClass: 'progress-circle-bg-default',
      };
    case 'default':
    default:
      return {
        strokeClass: 'progress-circle-color-default',
        backgroundClass: 'progress-circle-bg-default',
      };
  }
}

const ProgressCircle = forwardRef<SVGSVGElement, ProgressCircleProps>(
  (
    {
      value = 0,
      max = 100,
      size = 'medium',
      color = 'primary',
      variant = 'solid',
      className,
      children,
      showValue = false,
      animate = true,
      strokeWidthValue,
      ...props
    },
    ref
  ) => {
    const { radius, strokeWidth, fontSize } = sizeMapping[size];
    const { strokeClass, backgroundClass } = resolveColor(color);

    const safeValue = Math.min(max, Math.max(value, 0));
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const offset = circumference - (safeValue / max) * circumference;
    const percentage = Math.round((safeValue / max) * 100);

    const svgSize = radius * 2;

    const renderContent = () => {
      if (children) {
        return children;
      }
      if (showValue) {
        return <span className="progress-circle__value">{percentage}%</span>;
      }
      return null;
    };

    return (
      <div
        className={clsx(
          'progress-circle',
          `progress-circle-size-${size}`,
          `progress-circle-color-${color}`,
          `progress-circle-variant-${variant}`,
          className
        )}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={`${percentage}%`}
      >
        <svg
          ref={ref}
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          className="progress-circle__svg"
          {...props}
        >
          {/* Background circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            strokeWidth={strokeWidthValue || strokeWidth}
            fill="none"
            className={clsx('progress-circle__background', backgroundClass)}
          />

          {/* Progress circle */}
          {safeValue > 0 && (
            <circle
              cx={radius}
              cy={radius}
              r={normalizedRadius}
              strokeWidth={strokeWidthValue || strokeWidth}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={offset}
              className={clsx('progress-circle__progress', strokeClass, {
                'progress-circle__progress--animated': animate,
              })}
              transform={`rotate(-90 ${radius} ${radius})`}
            />
          )}
        </svg>

        {renderContent() && (
          <div className={clsx('progress-circle__content', fontSize)}>
            {renderContent()}
          </div>
        )}
      </div>
    );
  }
);

ProgressCircle.displayName = 'ProgressCircle';

export { ProgressCircle };
